<template>
  <div>
    <v-row dense>
      <v-spacer />
      <v-col cols="auto">
        <date-selector
          v-model="date"
          :label="$t('picker.date.title')"
        />
      </v-col>
      <v-col cols="auto">
        <layout-selector
          :selected-layout.sync="selectedLayout"
          :layouts.sync="layouts"
          @add-layout="openAddLayoutDialog"
          @edit-layout="openEditLayoutDialog"
        />
      </v-col>
    </v-row>
    <grid-layout
      v-if="selectedLayout"
      :key="selectedLayout.name"
      :layout="adjustedGridLayout"
      :col-num="12"
      :row-height="70"
      :is-draggable="false"
      :is-resizable="false"
      :vertical-compact="true"
      :margin="[10, 10]"
      :use-css-transforms="true"
      :use-transition="false"
      @layout-ready="layoutReady = true"
    >
      <grid-item
        v-for="item in adjustedGridLayout"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
      >
        <div
          v-if="layoutReady"
          class="fill-height"
        >
          <div
            v-if="item.type === 'table'"
            class="fill-height"
          >
            <v-card class="rounded-lg fill-height">
              <v-card-title>
                <v-row align="center">
                  <v-col>
                    Players
                  </v-col>
                  <v-col cols="auto">
                    <v-select
                      v-model="playerPositions.selectedId"
                      solo
                      dense
                      :label="$t('table_headers.player_overview.position')"
                      hide-details
                      clearable
                      item-value="id"
                      item-text="name"
                      :items="playerPositions.data"
                      style="width: 200px"
                    />
                  </v-col>
                  <v-col
                    cols="auto"
                    class="pl-2"
                  >
                    <v-select
                      v-model="playerStatuses.selectedId"
                      solo
                      dense
                      :label="$t('table_headers.player_overview.status')"
                      hide-details
                      clearable
                      :items="playerStatuses.data"
                      item-value="id"
                      item-text="name"
                      style="width: 200px"
                    />
                  </v-col>
                  <v-col
                    cols="auto"
                  >
                    <variable-selector
                      v-if="variables.data"
                      :variables="variables.data"
                      :layout-variables.sync="tableHeaders"
                      :show-aggregation="true"
                      :excluded-variables="['reason']"
                      @update="saveLayoutVariables"
                    />
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="layoutVariables"
                  :items="filteredSearchedPlayers"
                  :loading="players.loading"
                  hide-default-footer
                  :items-per-page="-1"
                  dense
                  :custom-sort="customSort"
                  sort-by="last_name"
                  :sort-desc="true"
                >
                  <template v-slot:item="{item}">
                    <player-overview-table-row
                      :item="item"
                      :layout-variables="layoutVariables"
                      :players="players"
                      @click="$router.push({ name: RouteNames.PLAYER_OVERVIEW, params: { id: item.id } })"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </div>
          <div
            v-else-if="item.type === 'bar_chart'"
            class="fill-height"
          >
            <performance-bar-chart
              :variable-key="item.key"
              :players="players.data"
            />
          </div>
          <div
            v-else-if="item.type === 'card'"
            class="fill-height"
          >
            <stat-card
              :loading="players.loading"
              :title="getVariableName(item.key)"
              :value="calculateAverage(item.key)"
              :max="100"
              :color="getColorForValue(calculateAverage(item.key), 100)"
            />
          </div>
          <div
            v-else-if="item.type === 'line_chart'"
            class="fill-height"
          >
            <load-trend-chart
              :variable-key="item.key"
              :stats="players.data"
              :title="getVariableName(item.key)"
            />
          </div>
          <div
            v-else-if="item.type === 'chart'"
            class="fill-height"
          >
            <performance-graph-individual
              :variables="variables.data"
              :chart="item"
              :stats="calculatedTeamStats"
              :height="String(item.h * 70 - 50)"
            />
            <team-load-chart
              class="fill-height"
              :players="players.data"
              :chart="item"
            />
            <performance-graph
              class="fill-height"
              :players="players.data"
              :today-stats="latestTeamStats"
              :yesterday-stats="yesterdayTeamStats"
              :chart="item"
            />
          </div>
        </div>
      </grid-item>
    </grid-layout>
  </div>
</template>

<script>
import {
  LayoutApiService,
  PlayerApiService,
  PlayerPositionApiService,
  PlayerStatusApiService, TeamApiService
} from '@/data/services'
import dayjs from 'dayjs'
import {
  adjustGridForTableHeights,
  getColorForValue,
  processMaxValues,
  processMinValues,
  splitVariable, toCamelCase
} from '@/utils/helpers'
import debounce from 'lodash/debounce'
import { DashboardType } from '@/data/models/Dashboard'
import { cloneDeep, orderBy } from 'lodash'
import { EventBus } from '@/plugins/bus'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import { GridItem, GridLayout } from 'vue-grid-layout'
import { VariableApiService } from '@/data/services/VariableApiService'
import StatCard from '@/components/StatCard.vue'
import LoadTrendChart from '@/components/charts/implementation/LoadTrendChart.vue'
import PerformanceBarChart from '@/components/charts/implementation/PerformanceBarChart.vue'
import PerformanceGraph from '@/components/performance/PerformanceGraph.vue'
import TeamLoadChart from '@/components/training-load/TeamLoadChart.vue'
import DateSelector from '@/components/DateSelector.vue'
import LayoutSelector from '@/components/LayoutSelector.vue'
import { dashboardConfig } from '@/data/models/DashboardConfig'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import PlayerOverviewTableRow from '@/components/players/PlayerOverviewTableRow.vue'
import { RouteNames } from '@/router/routes'

export default {
  name: 'OverviewView',
  components: {
    PlayerOverviewTableRow,
    PerformanceGraphIndividual,
    LayoutSelector,
    DateSelector,
    TeamLoadChart,
    PerformanceGraph,
    PerformanceBarChart,
    LoadTrendChart,
    StatCard,
    VariableSelector,
    GridLayout,
    GridItem
  },
  data: () => ({
    players: {
      data: [],
      loading: false
    },
    variables: {
      data: [],
      loading: false
    },
    playerPositions: {
      data: [],
      loading: false,
      selectedId: undefined
    },
    playerStatuses: {
      data: [],
      loading: false,
      selectedId: undefined
    },
    teamStats: {
      data: [],
      loading: false
    },
    date: null,
    selectedLayoutObject: null,
    selectedVariablesObject: null,
    layoutReady: false
  }),
  computed: {
    RouteNames() {
      return RouteNames
    },
    filteredSearchedPlayers() {
      return this.players.data.filter(player => {
        return this.playerStatuses.selectedId ? player.player_status_id === this.playerStatuses.selectedId : true
      }).filter(player => {
        return this.playerPositions.selectedId ? player.player_positions.map(pos => pos.id).includes(this.playerPositions.selectedId) : true
      })
    },
    layouts() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.TEAM_OVERVIEW)
    },
    layoutVariables() {
      return this.selectedLayout ? [{ sortable: false, class: 'pr-0' }, { value: 'last_name', text: this.$t('table_headers.name'), width: '200px' }, { value: 'player_position', text: this.$t('table_headers.player_overview.position'), width: '150px' }].concat(orderBy(this.tableHeaders.map(header => {
        return {
          ...header,
          value: header.value + (header.aggregation ? `_${header.aggregation}` : ''),
          text: this.getVariableName(header.value + (header.aggregation ? `_${header.aggregation}` : ''))
        }
      }), ['order'])) : []
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return cloneDeep(this.selectedLayoutObject)
        }
        return cloneDeep(this.layouts[0])
      },
      set(value) {
        if (value !== this.selectedLayoutObject) this.layoutReady = false
        this.selectedLayoutObject = value
      }
    },
    tableHeaders: {
      get() {
        if (this.selectedVariablesObject) {
          return this.selectedVariablesObject
        } else if (this.selectedLayout) {
          return this.selectedLayout.variables
        }
        return []
      },
      set(value) {
        this.selectedVariablesObject = value
      }
    },
    dayjs() {
      return dayjs
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    },
    hasItlModule() {
      return this.user && this.user.organisation_settings?.has_itl_module
    },
    hasGpsModule() {
      return this.user && this.user.organisation_settings?.has_gps_module
    },
    hasHrModule() {
      return this.user && this.user.organisation_settings?.has_heart_rate_module
    },
    hasNoPlayers() {
      return this.user && this.user.organisation_settings?.has_no_players
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    calculatedTeamStats() {
      if (!this.filteredSearchedPlayers || this.filteredSearchedPlayers.length === 0) {
        return {}
      }

      const teamStats = {}
      const samplePlayer = this.filteredSearchedPlayers.find(player => player.latest_stats)
      if (!samplePlayer) return {}

      const statKeys = Object.keys(samplePlayer.latest_stats)

      statKeys.forEach(key => {
        const values = this.filteredSearchedPlayers
          .map(player => player.latest_stats?.[key])
          .filter(value => typeof value === 'number' && !isNaN(value))

        if (values.length > 0) {
          const avg = values.reduce((sum, value) => sum + value, 0) / values.length
          teamStats[key] = Math.round(avg)
        } else {
          teamStats[key] = null
        }
      })

      if (teamStats.SLD) teamStats.SLD = (teamStats.SLD / 60).toFixed(2)
      if (teamStats.FAT) teamStats.FAT_perc = (((6 - teamStats.FAT) / 5) * 100).toFixed(0)
      if (teamStats.GMS) teamStats.GMS_perc = (((6 - teamStats.GMS) / 5) * 100).toFixed(0)
      if (teamStats.MOO) teamStats.MOO_perc = ((teamStats.MOO / 5) * 100).toFixed(0)
      if (teamStats.STL) teamStats.STL_perc = (((6 - teamStats.STL) / 5) * 100).toFixed(0)

      return teamStats
    },
    adjustedGridLayout() {
      if (!this.selectedLayout || !this.selectedLayout.grid) return []

      const tableRowCount = this.filteredSearchedPlayers?.length || 0

      return adjustGridForTableHeights(this.selectedLayout.grid, tableRowCount, 0)
    },
    latestTeamStats() {
      if (this.teamStats.data.length === 0) return {}

      return this.teamStats.data[this.teamStats.data.length - 1].stats
    },
    yesterdayTeamStats() {
      if (this.teamStats.data.length < 2) return {}

      return this.teamStats.data[this.teamStats.data.length - 2].stats
    }
  },
  watch: {
    date: function (val) {
      if (val) {
        this.fetchPlayers()
        this.fetchTeamStats()
      }
    },
    currentTeam(val) {
      if (val && val.id) {
        this.fetchPlayers()
        this.fetchTeamStats()
      }
    },
    selectedLayout() {
      this.selectedVariablesObject = null
    }
  },
  created() {
    if (this.hasNoPlayers || !(this.hasGpsModule || this.hasHrModule || this.hasItlModule)) {
      this.$router.push({ name: RouteNames.EVENTS })
    }

    this.date = this.$dayjs()

    this.fetchPlayers()
    this.fetchTeamStats()
    this.fetchVariables()
    this.fetchPlayerStatuses()
    this.fetchPlayerPositions()

    EventBus.$on(this.$events.LAYOUT_DELETED, (layout) => {
      this.selectedLayoutObject = this.layouts.filter(l => l.id !== layout.id)[0]
    })

    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })
  },
  methods: {
    getColorForValue,
    fetchPlayers: debounce(async function() {
      try {
        this.players.loading = true

        const queryParams = {
          team_id: this.currentTeam.id === 'all' ? undefined : this.currentTeam.id,
          stats_date: this.$dayjs(this.date).format('YYYY-MM-DD')
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data.map(player => {
          if (!player.latest_stats) {
            return player
          }

          if (player.latest_stats.SLD) player.latest_stats.SLD = (player.latest_stats.SLD / 60).toFixed(2)

          return player
        })

        this.players.maxValues = processMaxValues(players.map(player => player.latest_stats))
        this.players.minValues = processMinValues(players.map(player => player.latest_stats))

        this.players.data = players
      } catch (e) {
        this.players.data = []
        throw e
      } finally {
        this.players.loading = false
      }
    }, 50),
    fetchTeamStats: debounce(async function() {
      if (this.currentTeam.id === 'all' || !this.currentTeam.id) return

      this.teamStats.loading = true

      try {
        const teamStats = await TeamApiService.getStats(this.currentTeam.id, dayjs(this.date).subtract(7, 'days').format('YYYY-MM-DD'), dayjs(this.date).format('YYYY-MM-DD'))

        this.teamStats.data = teamStats.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    }),
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables({
          category: dashboardConfig[DashboardType.TEAM_OVERVIEW].variable_categories
        })

        this.variables.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    saveLayoutVariables: debounce(async function () {
      if (!this.hasLayoutManagement) return

      this.submitting = true

      try {
        const data = {
          variables: this.tableHeaders.map(v => ({
            id: v.id,
            order: v.order,
            aggregation: v.aggregation
          }))
        }

        await LayoutApiService.saveVariables(this.selectedLayout.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000),
    async fetchPlayerPositions() {
      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll({ sport_id: this.currentTeam.sport.id })

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: DashboardType.TEAM_OVERVIEW
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: DashboardType.TEAM_OVERVIEW,
        selectedLayout: this.selectedLayout
      })
    },
    getVariableName(value) {
      const [varKey, aggregation] = splitVariable(value)

      if (!varKey) {
        return this.$t(`variables.${aggregation}.long`)
      }

      return `${this.$t(`variables.${varKey}`)}: ${this.$t(`variables.${aggregation}.long`)}`
    },
    calculateAverage(key) {
      if (this.players.data.length === 0) return null

      const values = this.players.data.map(stat => stat.latest_stats[key]).filter(v => typeof v === 'number')

      if (values.length === 0) return null

      const avg = values.reduce((a, b) => a + b, 0) / values.length
      return Math.round(avg * 100) / 100
    },
    customSort(items, index, isDesc) {
      items.sort((a, b) => {
        let aValue = null
        let bValue = null

        if (index[0] === 'last_name') {
          aValue = a.last_name
          bValue = b.last_name
        } else {
          aValue = a.latest_stats[index]
          bValue = b.latest_stats[index]
        }

        if (isDesc[0]) {
          if (aValue === null || aValue === undefined) return -1
          if (bValue === null || bValue === undefined) return 1
          return aValue > bValue ? 1 : -1
        } else {
          if (aValue === null || aValue === undefined) return 1
          if (bValue === null || bValue === undefined) return -1
          return aValue < bValue ? 1 : -1
        }
      })
      return items
    }
  }
}
</script>
